import { useQuery } from '@tanstack/react-query'
import ms from 'ms'

import { useLayoutTransformed } from '@state/layout/queries'
import { withPriceLabels } from '@state/shelf/transformations/geometric'
import useViewContextAwareHttpClient from '@utils/ViewContextAwareHttpClient'
import { NotFoundError, type ProblemDetailsError } from '@utils/errors.ts'

import type { Shelf, ShelfDTO } from './types'

/**
  * more or less obsolete as we're loading the complete layout anyways
 */
export function useShelf(shelfId?: string | null) {
  const { get, storeId, viewKey } = useViewContextAwareHttpClient()
  const { data: layout } = useLayoutTransformed({ complete: true })

  return useQuery<Shelf | undefined, ProblemDetailsError>({
    queryKey: ['shelf', storeId, viewKey, shelfId],
    queryFn: async () => {
      const shelves = layout ? layout.shelves : []
      const shelf = shelves.find(shelf => shelf.id === shelfId)
      if (!shelf) throw new NotFoundError()

      // the day's shelf endpoint answers 404 for a shelf without an observation
      if (shelf.dayState === 'noData') return shelf

      // the neighbors part of this is a temporary fix for the fact that the ingestion process
      // does not properly map the left and right neighbor if a deploy db
      // scan is involved
      // the shelf/{shelfId} endpoint returns the correct neighbors even though it's technically
      // obsolete
      // basically relevant for preexisting scans that were created before we fixed the ingestion process
      // can probably be faced out after some time
      const shelfDto = await get<ShelfDTO>(`/shelf/${shelfId}`)
      const { leftNeighborShelfId, rightNeighborShelfId } = shelfDto

      return {
        ...withPriceLabels(shelf, shelfDto),
        leftNeighborShelfId,
        rightNeighborShelfId,
      }
    },
    enabled: !!shelfId && layout !== undefined,
    staleTime: ms('1d'),
  })
}